import React, { useCallback, useMemo } from 'react';
import { DateUtils, TypeUtils } from '@vectord/utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import clampDate from 'utils/clampDate';
import DateInput from 'components/DateInput';
import styles from './DateTimeInput.module.scss';
import TimeInput from 'components/TimeInput';

function DateTimeInput({
  className,
  compact = false,
  dateClassName,
  dateInputProps = {},
  disabled = false,
  onChange,
  required = false,
  timeClassName,
  timeInputProps = {},
  value,
  vertical = false,
  ...remainingProps
}) {
  const time = useMemo(() => {
    if (TypeUtils.isNullOrUndefined(value)) return null;

    const currentDate = new Date(value);
    const startOfDay = DateUtils.toStartOfDay(value);
    return currentDate.getTime() - startOfDay.getTime();
  }, [value]);

  const date = useMemo(() => {
    if (TypeUtils.isNullOrUndefined(value)) return null;

    const startOfDay = DateUtils.toStartOfDay(value);
    return startOfDay.getTime();
  }, [value]);

  const handleTimeChange = useCallback(
    (newTime) => {
      if (disabled) return;

      const [, hours, minutes, seconds] = DateUtils.toTimeUnits(newTime);
      const newDate = date ? new Date(date) : new Date();

      newDate.setMilliseconds(0);
      newDate.setSeconds(seconds);
      newDate.setMinutes(minutes);
      newDate.setHours(hours);

      const clampedDate = clampDate(newDate, {
        min: dateInputProps?.fromDate,
        max: dateInputProps?.toDate,
      });
      onChange(clampedDate.getTime());
    },
    [date, dateInputProps, disabled, onChange]
  );

  const handleDateChange = useCallback(
    (newDate) => {
      if (disabled) return;

      if (newDate) {
        const [, hours, minutes] = DateUtils.toTimeUnits(time);
        const newDateTime = new Date(newDate);

        newDateTime.setMilliseconds(0);
        newDateTime.setSeconds(0);
        newDateTime.setMinutes(minutes);
        newDateTime.setHours(hours);

        const clampedDate = clampDate(newDateTime, {
          min: dateInputProps?.fromDate,
          max: dateInputProps?.toDate,
        });
        onChange(new Date(clampedDate).getTime());
      } else {
        onChange(null);
      }
    },
    [dateInputProps, disabled, onChange, time]
  );

  return (
    <div
      {...remainingProps}
      className={classNames(
        styles.root,
        { [styles.horizontal]: !vertical },
        { [styles.vertical]: vertical },
        className
      )}
    >
      <DateInput
        {...dateInputProps}
        className={classNames(
          styles.date,
          { [styles.horizontalDate]: !vertical },
          { [styles.verticalDate]: vertical },
          dateClassName
        )}
        compact={compact}
        isDisabled={disabled}
        onChange={handleDateChange}
        required={required}
        value={date}
      />
      <TimeInput
        {...timeInputProps}
        className={classNames(styles.time, timeClassName)}
        compact={compact}
        isDisabled={disabled}
        onChange={handleTimeChange}
        required={required}
        value={time}
      />
    </div>
  );
}

DateTimeInput.propTypes = {
  className: PropTypes.string,
  compact: PropTypes.bool,
  dateClassName: PropTypes.string,
  dateInputProps: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  timeClassName: PropTypes.string,
  timeInputProps: PropTypes.object,
  // Epoch time in ms
  value: PropTypes.number,
  vertical: PropTypes.bool,
};

export default DateTimeInput;
